import { useEffect } from "react"
import { gql } from "@apollo/client"
import { getApolloInstance } from "apollo/client"
import { GetServerSideProps } from "next"
import NavbarLayout from "page-components/pallet/NavbarLayout"
import Welcome from "page-components/talent/welcome"
import { hasError } from "utils/apollo"
import {
    buildSSRRedirect,
    getPalletContext,
    PalletContext,
    parseBooleans,
} from "utils/router"
import {
    TalentWelcomeQuery,
    TalentWelcomeQuery_applylist,
} from "__generated__/TalentWelcomeQuery"
import { shouldUseFallbackVersion } from "page-components/talent/welcome/utils"
import { CandidateApplicationProvider } from "@pallet/features/onboardingL2/useCandidateApplication"
import { NavigationControlsProvider } from "@pallet/features/onboardingL2/useNavigationControls"
import { PALLET_HEAD_QUERY } from "page-components/pallet/NavbarLayout/PalletHead/queries"
import { PalletHeadQuery } from "__generated__/PalletHeadQuery"
import mixpanel from "mixpanel-browser"
import { EVENTS } from "constants/events"

export const TALENT_WELCOME_QUERY = gql`
    query TalentWelcomeQuery($slug: String!) {
        applylist(slug: $slug) {
            ... on ApplylistType {
                id
                canEdit
                customHireLink
                group {
                    id
                    canViewDrops
                    hasPendingApplication
                    hasPendingSubscription
                    activeMember {
                        id
                        applicationStatusEnum
                    }
                }
            }
            ...TalentWelcome_applylist
        }
    }
    ${Welcome.fragment}
`

export const getServerSideProps: GetServerSideProps = async context => {
    const apolloClient = getApolloInstance({ request: context.req })
    let palletContext = await getPalletContext(context.req)
    const slug = palletContext.pallet

    const { showPreview } = parseBooleans(context.query)

    const head = apolloClient.query<PalletHeadQuery>({
        query: PALLET_HEAD_QUERY,
        variables: { slug: slug ?? "" },
    })
    const pageQuery = apolloClient.query<TalentWelcomeQuery>({
        query: TALENT_WELCOME_QUERY,
        variables: { slug: slug ?? "" },
    })
    const queries = await Promise.all([head, pageQuery])
    const { data } = queries[1]

    if (hasError(queries) || !data.applylist?.group) {
        return {
            redirect: {
                permanent: false,
                destination: "https://pallet.com/",
            },
        }
    }

    if (!(showPreview && data.applylist.canEdit)) {
        if (data.applylist.group.canViewDrops) {
            return buildSSRRedirect(palletContext, "/talent/drops")
        }

        if (data.applylist.group.hasPendingSubscription) {
            return buildSSRRedirect(palletContext, "/talent/pending")
        }
    }

    return {
        props: {
            applylist: data.applylist,
            slug,
            palletContext,
            initialApolloState: apolloClient.cache.extract(),
        },
    }
}

type WelcomePageProps = {
    applylist: TalentWelcomeQuery_applylist
    slug: string
    palletContext: PalletContext
}

const WelcomePage = ({ applylist, slug, palletContext }: WelcomePageProps) => {
    useEffect(() => {
        mixpanel.track(EVENTS.VIEW_TALENT_WELCOME, {
            pallet: slug,
        })
    })

    return (
        <NavbarLayout
            collectiveHead
            palletContext={palletContext}
            showFooter={!shouldUseFallbackVersion(applylist)}
        >
            <CandidateApplicationProvider>
                <NavigationControlsProvider>
                    <Welcome slug={slug} />
                </NavigationControlsProvider>
            </CandidateApplicationProvider>
        </NavbarLayout>
    )
}

export default WelcomePage
